import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Download, Loader2, Wallet, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { eur } from "@/lib/format";
import { UpgradeModal } from "@/components/airep/UpgradeModal";
import api from "@/lib/api";
import { yellowBtn, TYPES, downloadFile } from "@/lib/airep";
import { Results } from "@/components/airep/Results";

const Block = ({ title, items }) => (items?.length ? (
  <div><h3 className="font-display font-bold text-lg mb-2">{title}</h3><ul className="space-y-1.5 text-sm text-slate-700">{items.map((k, i) => <li key={i} className="flex gap-2"><span className="h-1.5 w-1.5 rounded-full bg-slate-900 mt-2 shrink-0" />{k}</li>)}</ul></div>
) : null);

function ExtraReportModal({ d, busy, onClose, onConfirm }) {
  const navigate = useNavigate();
  const low = d.insufficient || d.balance_cents < d.total_cents;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white text-slate-900 rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="extra-report-modal">
        <div className="flex items-start justify-between gap-3"><h3 className="font-display text-xl font-bold">Report AI extra</h3><button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-full bg-[#E3FF00]" data-testid="extra-report-close"><X className="h-4 w-4" /></button></div>
        <p className="text-sm text-slate-600">Hai usato i <b>{d.included}</b> report AI inclusi nel tuo piano questo mese. Questo report costa <b>{eur(d.price_cents)} + IVA</b> (<b>{eur(d.total_cents)}</b>) e viene scalato dal Borsellino.</p>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm flex items-center gap-2" data-testid="extra-report-balance"><Wallet className="h-4 w-4" /> Saldo borsellino: <b>{eur(d.balance_cents)}</b></div>
        {low ? <>
          <p className="text-sm text-rose-600">Saldo insufficiente: ricarica il borsellino per continuare.</p>
          <button onClick={() => navigate("/messages")} className={`${yellowBtn} w-full py-3`} data-testid="extra-report-recharge">Ricarica borsellino</button>
        </> : <button disabled={busy} onClick={onConfirm} className={`${yellowBtn} w-full py-3`} data-testid="extra-report-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Conferma e genera ({eur(d.total_cents)})</button>}
        <button onClick={() => navigate("/ai-report/app?tab=plan")} className={`${yellowBtn} w-full py-2.5 text-sm`} data-testid="extra-report-upgrade">Passa a un piano superiore</button>
      </div>
    </div>
  );
}

export default function AiReportSession() {
  const { sid } = useParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [extra, setExtra] = useState(null);
  const load = () => api.get(`/airep/sessions/${sid}`).then(({ data }) => setS(data));
  useEffect(() => { load(); }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const gen = async (confirm_extra = false) => {
    setBusy(true);
    try { await api.post(`/airep/sessions/${sid}/report`, { confirm_extra }); setExtra(null); await load(); toast.success(confirm_extra ? "Report AI extra pronto: addebitato dal borsellino" : "Report AI pronto"); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "extra_report") setExtra(d);
      else if (e?.response?.status === 402) setUpgrade(d);
      else toast.error(typeof d === "string" ? d : "Generazione non riuscita");
    }
    setBusy(false);
  };
  const r = s.report;
  return (
    <div className="max-w-5xl space-y-6 pb-24" data-testid="airep-session">
      {upgrade && <UpgradeModal reason={upgrade} onClose={() => setUpgrade(null)} onDone={load} />}
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/ai-report/app" className="h-10 w-10 grid place-items-center rounded-xl bg-[#E3FF00]" data-testid="session-back"><ArrowLeft className="h-4 w-4" /></Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold truncate">{s.title}</h1>
          <p className="text-sm text-slate-500">Codice {s.code} · {s.participants} partecipanti · {s.status === "live" ? "in corso" : "terminata"}</p>
        </div>
        {s.status === "live" && <Link to={`/ai-report/present/${sid}`} className={`${yellowBtn} px-4 py-2.5 text-sm`} data-testid="session-resume">Torna alla presentazione</Link>}
      </div>
      <div className="rounded-2xl p-5 sm:p-7 bg-[#0b0f14] text-white border border-[#212d3b]" data-testid="session-ai-box">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#E3FF00]" /> Report AI</h2>
          <div className="flex gap-2">
            <button onClick={() => gen()} disabled={busy} className={`${yellowBtn} px-4 py-2.5 text-sm`} data-testid="session-generate-report">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{r ? "Rigenera" : "Genera report AI"}</button>
            {r && <button onClick={() => downloadFile(`/airep/sessions/${sid}/report.pdf`, `ai-report-${s.code}.pdf`)} className={`${yellowBtn} px-4 py-2.5 text-sm`} data-testid="session-download-pdf"><Download className="h-4 w-4" /> PDF</button>}
          </div>
        </div>
        {busy && <p className="text-sm text-slate-400 mt-4">L'AI sta analizzando le risposte… (circa 20–40 secondi)</p>}
        {extra && <ExtraReportModal d={extra} busy={busy} onClose={() => setExtra(null)} onConfirm={() => gen(true)} />}
        {!r && !busy && <p className="text-sm text-slate-400 mt-3">Sintesi, temi principali, punti chiave e raccomandazioni generati dalle risposte del pubblico.</p>}
      </div>
      {r && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7 space-y-6" data-testid="session-report">
          <div><h3 className="font-display text-2xl font-extrabold">{r.title}</h3><p className="text-slate-700 mt-2 leading-relaxed" data-testid="session-report-summary">{r.summary}</p></div>
          <Block title="Punti chiave" items={r.key_points} />
          {r.themes?.length > 0 && <div><h3 className="font-display font-bold text-lg mb-2">Temi emersi</h3><div className="grid sm:grid-cols-2 gap-3">{r.themes.map((t, i) => <div key={i} className="rounded-xl border border-slate-200 p-4"><div className="font-semibold">{t.name}</div><div className="text-sm text-slate-600 mt-1">{t.description}</div></div>)}</div></div>}
          <Block title="Raccomandazioni" items={r.recommendations} />
        </div>
      )}
      <div className="space-y-4">
        {(s.all || []).map(({ slide, results }, i) => (
          <div key={slide.id} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7" data-testid={`session-slide-${i}`}>
            <span className="text-xs uppercase tracking-widest text-slate-500">{TYPES[slide.type]} · {results.total} risposte</span>
            <h3 className="font-display text-xl font-bold mt-1 mb-5">{slide.question}</h3>
            <Results slide={slide} results={results} />
          </div>
        ))}
      </div>
    </div>
  );
}
